import { ComposedTheme, createUseStyles, useTheme } from '@glu/theming';

const styles = ({ buttons }: ComposedTheme) => ({
  root: {
    cursor: buttons.cursor,
    fontFamily: buttons.fontFamily,
    outlineOffset: buttons.outlineOffset,
    borderStyle: 'solid',
    lineHeight: 1,
    borderWidth: 1,

    '&[aria-busy]': {
      position: 'relative',
      overflow: 'hidden',

      '&::before': {
        content: '""',
        boxSizing: 'border-box',
        position: 'absolute',
        display: 'inline-block',
        left: '50%',
        opacity: 0,
        top: '80%',
        border: '2px solid',
        borderRadius: '50%',
        transitionDuration: '0.3s',
        transitionProperty: 'opacity, top',
        animationDuration: '1s',
        animationIterationCount: 'infinite',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        animationName: `${useTheme ? '$' : ''}rotate`,
        animationTimingFunction: 'linear',
      },

      '&:after': {
        content: '""',
        display: 'inline-block',
        height: '100%',
        width: 0,
      },

      '&[aria-busy="true"]': {
        pointerEvents: 'none',
        cursor: 'not-allowed',
        color: 'transparent',
        textAlign: 'left',

        '&::before': {
          opacity: 1,
          top: '50%',
          transitionDuration: '0.5s',
        },
      },
    },
  },

  normal: {
    borderRadius: buttons.normal.borderRadius,
    fontSize: buttons.normal.fontSize,
    padding: buttons.normal.padding,
    '&[aria-busy]::before': {
      marginTop: -(buttons.normal.spinnerSize / 2),
      marginLeft: -(buttons.normal.spinnerSize / 2),
      width: buttons.normal.spinnerSize,
      height: buttons.normal.spinnerSize,
    },
  },

  small: {
    borderRadius: buttons.small.borderRadius,
    fontSize: buttons.small.fontSize,
    padding: buttons.small.padding,
    '&[aria-busy]::before': {
      marginTop: -(buttons.small.spinnerSize / 2),
      marginLeft: -(buttons.small.spinnerSize / 2),
      width: buttons.small.spinnerSize,
      height: buttons.small.spinnerSize,
    },
  },

  extraSmall: {
    borderRadius: buttons.extraSmall.borderRadius,
    fontSize: buttons.extraSmall.fontSize,
    padding: buttons.extraSmall.padding,
    '&[aria-busy]::before': {
      marginTop: -(buttons.extraSmall.spinnerSize / 2),
      marginLeft: -(buttons.extraSmall.spinnerSize / 2),
      width: buttons.extraSmall.spinnerSize,
      height: buttons.extraSmall.spinnerSize,
    },
  },

  primary: {
    backgroundColor: buttons.primary.backgroundColor,
    borderColor: buttons.primary.borderColor,
    color: buttons.primary.textColor,
    textTransform: buttons.primary.textTransform,
    fontWeight: buttons.primary.fontWeight,
    '&:hover': {
      backgroundColor: buttons.primary.hoverColor,
      borderColor: buttons.primary.hoverColor,
    },

    '&:focus': {
      ...(buttons.primary.outlineReplacement ? { outline: 'none' } : undefined),
      boxShadow: buttons.primary.outlineReplacement,
      backgroundColor: buttons.primary.focusColor,
      borderColor: buttons.primary.focusColor,
    },

    '&[aria-busy="true"]': {
      backgroundColor: buttons.primary.spinnerBackground,
      borderColor: buttons.primary.spinnerBorder,

      '&:before': {
        borderColor: [
          buttons.primary.spinnerColor,
          buttons.primary.spinnerColor,
          buttons.primary.spinnerColor,
          'transparent',
        ],
      },
    },

    '&:disabled': {
      backgroundColor: buttons.primary.disabledBackground,
      borderColor: buttons.primary.disabledBorder,
      color: buttons.primary.disabledColor,
      cursor: 'not-allowed',
      '& $icon svg': {
        fill: buttons.primary.disabledColor,
      },
    },
    '& $icon svg': {
      fill: buttons.primary.textColor,
    },
  },
  secondary: {
    backgroundColor: buttons.secondary.backgroundColor,
    borderColor: buttons.secondary.borderColor,
    color: buttons.secondary.textColor,
    textTransform: buttons.secondary.textTransform,
    fontWeight: buttons.secondary.fontWeight,

    '&:focus, &:active': {
      backgroundColor: buttons.secondary.focusColor,
      borderColor: buttons.secondary.focusBorderColor,
    },

    '&:focus': {
      ...(buttons.primary.outlineReplacement ? { outline: 'none' } : undefined),
      boxShadow: buttons.primary.outlineReplacement,
    },

    '&:hover': {
      backgroundColor: buttons.secondary.hoverColor,
      borderColor: buttons.secondary.borderColor,
    },

    '&[aria-busy="true"]': {
      backgroundColor: buttons.secondary.spinnerBackground,
      borderColor: buttons.secondary.spinnerBorder,

      '&:before': {
        borderColor: [
          buttons.secondary.spinnerColor,
          buttons.secondary.spinnerColor,
          buttons.secondary.spinnerColor,
          'transparent',
        ],
      },
    },

    '&:disabled': {
      backgroundColor: buttons.secondary.disabledBackground,
      borderColor: buttons.secondary.disabledBorder,
      color: buttons.secondary.disabledColor,
      cursor: 'not-allowed',
      '& $icon svg': {
        fill: buttons.secondary.disabledColor,
      },
    },
    '& $icon svg': {
      fill: buttons.secondary.textColor,
    },
  },
  tertiary: {
    backgroundColor: buttons.tertiary.backgroundColor,
    border: 'none',
    borderRadius: 0, // border: none doesn't remove border radius
    textDecoration: buttons.tertiary.textDecoration,
    color: buttons.tertiary.textColor,
    padding: buttons.tertiary.padding,

    '&:focus, &:active': {
      backgroundColor: buttons.tertiary.focusColor,
      textDecoration: buttons.tertiary.focusTextDecoration,
      outlineColor: buttons.focusColor,
    },

    '&:hover': {
      backgroundColor: buttons.tertiary.hoverColor,
      textDecoration: buttons.tertiary.hoverTextDecoration,
    },

    '&:disabled': {
      color: buttons.tertiary.disabledColor,
      textDecoration: 'none',
      cursor: 'not-allowed',
      '& $icon svg': {
        fill: buttons.tertiary.disabledColor,
      },
    },
    '& $icon svg': {
      fill: buttons.tertiary.textColor,
    },
  },

  block: {
    backgroundColor: buttons.block.backgroundColor,
    color: buttons.block.textColor,
    border: 'none',
    width: '100%',
    borderRadius: 0,
    height: 54,
    padding: 0,
    borderLeft: `1px solid ${buttons.block.borderColor}`,
    borderRight: `1px solid ${buttons.block.borderColor}`,

    '&:first-child': {
      borderLeft: 0,
    },

    '&:last-child': {
      borderRight: 0,
    },

    '&:hover': {
      backgroundColor: buttons.block.hoverBackgroundColor,
    },

    '&:focus': {
      backgroundColor: buttons.block.focusBackgroundColor,
    },

    '&[aria-busy="true"]': {
      '&:before': {
        borderColor: [
          buttons.primary.spinnerColor,
          buttons.primary.spinnerColor,
          buttons.primary.spinnerColor,
          'transparent',
        ],
      },
    },

    '&:disabled': {
      backgroundColor: buttons.block.disabledBackgroundColor,
      borderColor: buttons.block.disabledBorderColor,
      color: buttons.block.disabledTextColor,
      cursor: 'not-allowed',
      '& $icon svg': {
        fill: buttons.block.disabledTextColor,
      },
    },
    '& $icon svg': {
      fill: buttons.block.textColor,
    },
  },

  '@keyframes rotate': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  icon: {
    fontSize: 0,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftIcon: {
    marginRight: buttons.icon.iconMargin,
  },
  rightIcon: {
    marginLeft: buttons.icon.iconMargin,
  },
  prominent: {
    color: buttons.prominentColor,
  },
});

export default createUseStyles(styles);
